/* System defined core library */
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { Subscription, Subject } from 'rxjs';

/* User defined library/components */
import { RoutingService } from '../../../shared/services/routing.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';

/* User defined models */
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { AppointmentSlot } from '../../../shared/models/scheduling/appointmentSlot.model';
import { Client } from '../../../shared/models/scheduling/client.model';
import { Country } from '../../../shared/models/scheduling/country.model';
import { Exam } from '../../../shared/models/scheduling/exam.model';
import { Hold } from '../../../shared/models/scheduling/hold.model';
import { LocationSelector } from '../../../shared/models/scheduling/locationSelector.model';
import { MetricData, MetricDetails } from '../../../shared/models/scheduling/metrics.model';
import { Program } from '../../../shared/models/scheduling/program.model';
import { State } from '../../../shared/models/scheduling/state.model';

/* User defined enums/constants */
import { ComponentType, MetricType } from '../../../shared/enums/scheduling.enum';
import { BootstrapParams } from '../../../../bootstrap-params';
import { LocationSelectorComponent } from '../../shared/components/location-selector/location-selector.component';


@Component({
    selector: 'app-reschedule-location',
    templateUrl: './reschedule-location.component.html'
})
export class RescheduleLocationComponent implements OnInit {

    /* User defined private/public variables */
    public client: Client;
    public endDateError: string = '';
    public exam: Exam;
    public firstTimeSearch: boolean;
    public typeaheadNoResults: boolean = false;
    public isCurrentFormChanged: boolean;
    public isLocationSelected: boolean = false;
    public metricDetails: MetricDetails;
    public program: Program;
    public region: State;
    public selectedAppointments: AppointmentDetail[] = [];
    public selectedCountry: Country;
    public selectedEndDate: any;
    public selectedIndex: number = 0;
    public selectedStartDate: any;
    public selectedState: State;
    public startDateError: string = '';
    public isGoogleMapValid: boolean = true;
    public userCaptchaInput: string = '';


    /* Service subscriptions */
    client$: Subscription;
    country$: Subscription;
    exam$: Subscription;
    firstTimeSearch$: Subscription;
    metricDetails$: Subscription;
    program$: Subscription;
    region$: Subscription;
    selectedAppointments$: Subscription;
    selectedEndDate$: Subscription;
    selectedLocation$: Subscription;
    selectedStartDate$: Subscription;
    state$: Subscription;

    @ViewChild(LocationSelectorComponent) locationSelectorInstance: LocationSelectorComponent;

    /* Constructor declaration with required injected services */
    constructor(
        private selectedExamDetailService: SelectedExamDetailService,
        private routingService: RoutingService,
        private utilityService: UtilityService,
        private bootstrapParams: BootstrapParams
    ) {
        this.client$ = selectedExamDetailService.client.subscribe(
            client => {
                this.client = client;
            });

        this.country$ = selectedExamDetailService.country.subscribe(
            country => {
                this.selectedCountry = country;
            });

        this.exam$ = selectedExamDetailService.exam.subscribe(
            exam => {
                this.exam = exam;
            });

        this.firstTimeSearch$ = this.selectedExamDetailService.firstTimeSearch.subscribe(
            firstTimeSearch => {
                this.firstTimeSearch = firstTimeSearch;
            });

        this.metricDetails$ = selectedExamDetailService.metricDetails.subscribe(
            metricDetails => {
                this.metricDetails = metricDetails;
            });

        this.program$ = selectedExamDetailService.program.subscribe(
            program => {
                this.program = program;
            });

        this.isGoogleMapValid = this.selectedExamDetailService.isGoogleMapsValid;

        this.region$ = selectedExamDetailService.region.subscribe(
            region => {
                this.region = region;
            });

        this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
            selectedAppointments => {
                this.selectedAppointments = selectedAppointments;
            });

        this.selectedEndDate$ = selectedExamDetailService.selectedEndDate.subscribe(
            selectedEndDate => {
                this.selectedEndDate = selectedEndDate;
            });

        this.selectedStartDate$ = selectedExamDetailService.selectedStartDate.subscribe(
            selectedStartDate => {
                this.selectedStartDate = selectedStartDate;
            });

        this.state$ = selectedExamDetailService.state.subscribe(
            state => {
                this.selectedState = state;
            });

        this.selectedIndex = this.selectedExamDetailService.selectedAppointmentIndex;


        if (this.region != null && this.region != undefined) {
            this.isLocationSelected = true;
        }

        this.selectedExamDetailService.getUserCaptchaInput().subscribe(captcha => {
            this.userCaptchaInput = captcha;
        });
    }

    /* Initialize the component & call required methods when component loaded first time. */
    ngOnInit() {
        this.isCurrentFormChanged = false;
        sessionStorage.setItem('previousUrl', window.location.pathname);
        if (!this.utilityService.isDataValidToLoadComponent(ComponentType.RescheduleLocationComponent)) { return; }

        if (this.selectedAppointments == null || this.selectedAppointments == undefined) {
            this.selectedAppointments = [];
            const tmpAppointmentDetail: AppointmentDetail = new AppointmentDetail();
            tmpAppointmentDetail.client = this.client;
            tmpAppointmentDetail.program = this.program;
            tmpAppointmentDetail.exam = this.exam;
            this.selectedAppointments.push(tmpAppointmentDetail);
            this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
        }
    }

    /* Use to move back to previous screen */
    goBack(): void {
        if (!this.isCurrentFormChanged) {
            this.selectedExamDetailService.setSelectedLocation(null);
        }

        const seatval: LocationSelector = {
            region: this.region,
            selectedStartDate: this.selectedStartDate,
            selectedEndDate: this.selectedEndDate
        };

        this.selectedExamDetailService.setSelectedLocation(seatval);

        this.selectedIndex -= 1;
        this.selectedExamDetailService.selectedAppointmentIndex = this.selectedIndex;
        this.selectedExamDetailService.appointmentCountForInfo.next(this.selectedExamDetailService.selectedAppointmentIndex);

        if (this.selectedExamDetailService.selectedAppointmentIndex > -1) {

            this.selectedAppointments[this.selectedIndex].region = (this.region != null && this.region != undefined) ?
                this.region : this.selectedAppointments[this.selectedIndex].region;

            this.selectedAppointments[this.selectedIndex].selectedStartDate =
                (this.selectedStartDate != null && this.selectedStartDate != undefined) ?
                    this.selectedStartDate : this.selectedAppointments[this.selectedIndex].selectedStartDate;

            this.selectedAppointments[this.selectedIndex].selectedEndDate =
                (this.selectedEndDate != null && this.selectedEndDate != undefined) ?
                    this.selectedEndDate : this.selectedAppointments[this.selectedIndex].selectedEndDate;

            this.selectedExamDetailService.setSelectedStartDate(this.selectedAppointments[this.selectedIndex].selectedStartDate);
            this.selectedExamDetailService.setSelectedEndDate(this.selectedAppointments[this.selectedIndex].selectedEndDate);

            this.selectedExamDetailService.setSlot(null); // Set previous slot NULL.
            this.selectedExamDetailService.setHold(null); // Set previous Hold NULL.

            this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
            this.selectedExamDetailService.setSlot(null);
            this.selectedExamDetailService.setSiteSlot(null);
        }
        this.routingService.backwardNavigation(ComponentType.RescheduleLocationComponent);
    }

    /* Use to move next screen */
    submit(event: any): void {
        if(this.bootstrapParams.captchaEnable && this.locationSelectorInstance){
            this.locationSelectorInstance.validateCaptcha();
            this.selectedExamDetailService.getCaptchaValidationResult().subscribe(isCaptchaValid => {
                if (isCaptchaValid) {
                    this.proceedToNextPage();
                }
            });
        }
        else {
            this.proceedToNextPage();
        }
        
    }

    proceedToNextPage(){
        const seatval: LocationSelector = {
            region: this.region,
            selectedStartDate: this.selectedStartDate,
            selectedEndDate: this.selectedEndDate
        };
        this.selectedExamDetailService.setSelectedLocation(seatval);

        if (this.selectedIndex > -1) {
            this.selectedAppointments[this.selectedIndex].region = this.region;
            this.selectedAppointments[this.selectedIndex].selectedStartDate = this.selectedStartDate;
            this.selectedAppointments[this.selectedIndex].selectedEndDate = this.selectedEndDate;
            this.selectedAppointments[this.selectedIndex].appointmentSlot = new AppointmentSlot();
            this.selectedAppointments[this.selectedIndex].hold = new Hold();
        }
        this.selectedExamDetailService.setSlot(null); // Set previous slot NULL.
        this.selectedExamDetailService.setHold(null); // Set previous Hold NULL.

        this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
        this.selectedExamDetailService.setSlot(null);
        this.selectedExamDetailService.setSiteSlot(null);
        this.routingService.rescheduleFwdNavigation(ComponentType.RescheduleLocationComponent);
    }

    /* disable next button on the basis of local variable value */
    public isDisable(): boolean {
        return (
            !this.isLocationSelected ||
            this.selectedStartDate == null
            || (this.endDateError != '')
            || (this.startDateError != '')
            || (this.userCaptchaInput == '' && this.bootstrapParams.captchaEnable)
            || (this.isGoogleMapValid && ((this.typeaheadNoResults) || (this.region == null))))
    }

    /* hide prev button on the basis of service variable value */
    public canHideButton(): boolean {
        return (this.selectedExamDetailService.setReschedulePathLock && this.selectedExamDetailService.skipAppointmentLookup);
    }

    public getErrorMessage(event: any): void {
        this.endDateError = event.endDateError;
        this.typeaheadNoResults = event.typeaheadNoResults;
        this.startDateError = event.startDateError;
    }

    public getSelectedLocation(event: any): void {
        this.isLocationSelected = event.isLocationSelected;
        this.isCurrentFormChanged = event.isCurrentFormChanged;
    }

    validRegionSelector(event: any) {
        this.isLocationSelected = event;
    }

    /* Set MetricDetails in shared service */
    setMetricDetails(): void {
        this.metricDetails = new MetricDetails();
        if (this.region) {
            this.metricDetails.searched = new MetricData(
                MetricType.Searched,
                { 'latitude': this.region.lat, 'longitude': this.region.long },
                this.utilityService.getFormatToMMDDYYYY(new Date(this.selectedStartDate['jsdate']), '-')
            );
            if (!this.firstTimeSearch) {
                this.selectedExamDetailService.setFirstTimeSearch(true);
                this.selectedExamDetailService.setMetricDetails(this.metricDetails);
            } else {
                this.selectedExamDetailService.setFirstTimeSearch(false);
            }
        }
    }
}
